import { AnimatePresence, motion } from "framer-motion";
import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../lib/utils";

interface Command {
	id: string;
	label: string;
	icon: React.ReactNode;
	shortcut?: string;
	action: () => void;
}

interface CommandPaletteProps {
	open: boolean;
	onClose: () => void;
	commands: Command[];
}

export function CommandPalette({ open, onClose, commands }: CommandPaletteProps) {
	const [query, setQuery] = useState("");
	const [selectedIndex, setSelectedIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);

	const filtered = useMemo(() => {
		if (!query.trim()) return commands;
		const q = query.toLowerCase();
		return commands.filter((c) => c.label.toLowerCase().includes(q));
	}, [query, commands]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset selection when query changes
	useEffect(() => {
		setSelectedIndex(0);
	}, [query]);

	useEffect(() => {
		if (open) {
			setQuery("");
			inputRef.current?.focus();
		}
	}, [open]);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				onClose();
			}
			if (!open) return;
			if (e.key === "Escape") onClose();
			if (e.key === "ArrowDown") {
				e.preventDefault();
				setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
			}
			if (e.key === "ArrowUp") {
				e.preventDefault();
				setSelectedIndex((i) => Math.max(i - 1, 0));
			}
			if (e.key === "Enter") {
				e.preventDefault();
				const cmd = filtered[selectedIndex];
				if (cmd) {
					cmd.action();
					onClose();
				}
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [open, onClose, filtered, selectedIndex]);

	return (
		<AnimatePresence>
			{open && (
				<>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/50 z-50"
						onClick={onClose}
					/>
					<motion.div
						initial={{ opacity: 0, scale: 0.97, y: 10 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.97, y: 10 }}
						className="fixed top-[20%] left-1/2 -translate-x-1/2 w-[90vw] max-w-lg z-[60]"
					>
						<div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
							<div className="flex items-center gap-3 px-4 py-3 border-b border-border">
								<Search className="w-4 h-4 text-muted-foreground shrink-0" />
								<input
									ref={inputRef}
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									placeholder="Type a command or search..."
									className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
								/>
								<kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-secondary text-muted-foreground font-mono">
									ESC
								</kbd>
							</div>
							<div className="max-h-[60vh] overflow-y-auto py-1">
								{filtered.length === 0 ? (
									<div className="px-4 py-8 text-center text-sm text-muted-foreground">
										No commands found.
									</div>
								) : (
									filtered.map((cmd, i) => (
										<button
											key={cmd.id}
											type="button"
											onClick={() => {
												cmd.action();
												onClose();
											}}
											className={cn(
												"w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left",
												i === selectedIndex ? "bg-primary/10 text-primary" : "hover:bg-secondary/50"
											)}
										>
											{cmd.icon}
											<span className="flex-1">{cmd.label}</span>
											{cmd.shortcut && (
												<kbd className="hidden sm:inline-flex px-1.5 py-0.5 rounded text-[10px] bg-secondary text-muted-foreground font-mono">
													{cmd.shortcut}
												</kbd>
											)}
										</button>
									))
								)}
							</div>
						</div>
					</motion.div>
				</>
			)}
		</AnimatePresence>
	);
}
